import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, Detail, Pill, PillVariant, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {NovaVisuallyHidden} from '../shared/novaVisuallyHidden';
import {NovaButtonSizer} from './NovaButtonSize';

const tokens = tokensForThemeProvider.sky.sys;

// One insertable entry: a token, what it is, and optionally where it comes from.
export interface InsertLibraryRow {
	key: string;
	token: string;
	description: string;
	secondary?: string;
	badges?: string[];
	// When set, the token is shown disabled and cannot be inserted; this is the reason, given on hover,
	// as the button's accessible description, and once under its group's label.
	disabledReason?: string | null;
	dataCy: string;
	onInsert: () => void;
}

export interface InsertLibraryGroup {
	key: string;
	label: string;
	rows: InsertLibraryRow[];
	// Shown in place of rows when the group has none.
	emptyText?: string;
}

interface NovaInsertLibraryProps {
	// Prefix for every data-cy on the panel: `<prefix>`, `<prefix>-bar`, `<prefix>-body`,
	// `<prefix>-group-<key>`, `<prefix>-group-<key>-blocked`, `<prefix>-<key lower case>-empty`,
	// `<prefix>-target`, `<prefix>-notice` and `<prefix>-empty`.
	dataCy: string;
	title: string;
	// One line beside the title saying what a click does.
	helper: string;
	countLabel: string;
	// The prompt a click inserts into, shown as a pill beside the count. Omitted, there is no pill.
	targetLabel?: string;
	groups: InsertLibraryGroup[];
	// Shown above the groups, for a list that could not be read in full.
	notice?: string;
	// A button beside the notice that reads the list again.
	noticeAction?: {label: string; onPress: () => void};
	// Shown in place of the groups when there are none.
	emptyText?: string;
}

const Container = styled.div`
	border: 1px solid ${tokens.color.border.decorative};
	border-radius: 8px;
	overflow: hidden;
`;

const NoticeRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px;
`;

const Helper = styled.span`
	min-width: 0;
`;

// The target pill and the count, pushed to the right end of the header.
const HeaderEnd = styled.span`
	margin-left: auto;
	display: flex;
	align-items: center;
	gap: 8px;
	padding-right: 8px;
`;

const Body = styled.div`
	border-top: 1px solid ${tokens.color.border.decorative};
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const Group = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

// The group heading: a small uppercase caption in a light grey.
const GroupLabel = styled.span`
	display: block;
	text-transform: uppercase;
	letter-spacing: 1.2px;

	& * {
		color: ${tokens.color.text.disabled.ondisabled};
	}
`;

// The token takes two fifths of the row and its description the rest, so tokens line up down the list.
const Row = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
	gap: 16px;
	align-items: center;
`;

const TokenCell = styled.div`
	display: flex;
	min-width: 0;
`;

const DescriptionCell = styled.div`
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

// Pressing the bar or a token keeps the prompt being edited focused, so an insertion lands at its
// caret. The capture handlers run before the button's own and stop the browser moving focus; the
// keyboard still reaches every button.
const keepFocus = {
	onMouseDownCapture: (event: React.MouseEvent) => event.preventDefault(),
	onPointerDownCapture: (event: React.PointerEvent) => event.preventDefault(),
};

// A collapsible list of tokens an author can drop into the prompt they are editing: closed by
// default, opened from its header, which says how many entries there are.
// The reason a group's tokens are disabled, shown once under its label so it can be read without
// hovering. Only when every row is disabled for that one reason: a group with rows that can still be
// inserted gives each disabled row its own reason instead.
function blockedReasonOf(group: InsertLibraryGroup): string | null {
	const [first] = group.rows;
	const reason = first?.disabledReason ?? null;
	return reason && group.rows.every(row => row.disabledReason === reason) ? reason : null;
}

// The element that gives a disabled row its reason: the line under its group when the row shares it,
// so the reason is read once per group rather than after every row, or the row's own hidden copy.
function reasonIdOf(row: InsertLibraryRow, group: InsertLibraryGroup, dataCy: string): string | undefined {
	if (!row.disabledReason) {
		return undefined;
	}
	return row.disabledReason === blockedReasonOf(group) ? `${dataCy}-group-${group.key}-blocked` : `${row.dataCy}-reason`;
}

export function NovaInsertLibrary({
	dataCy,
	title,
	helper,
	countLabel,
	targetLabel,
	groups,
	notice,
	noticeAction,
	emptyText,
}: NovaInsertLibraryProps): JSX.Element {
	const [expanded, setExpanded] = useState(false);

	return (
		<Container data-cy={dataCy} data-testid={dataCy}>
			<Header>
				<NovaButtonSizer $size="header" {...keepFocus}>
					<Button
						emphasis="low"
						icon={expanded ? 'chevronDown' : 'chevronRight'}
						aria-expanded={expanded}
						data-cy={`${dataCy}-bar`}
						onPress={() => setExpanded(previous => !previous)}
					>
						{title}
					</Button>
				</NovaButtonSizer>
				<Helper data-cy={`${dataCy}-helper`}>
					<Text color="auxiliary">{helper}</Text>
				</Helper>
				<HeaderEnd>
					{targetLabel && (
						<span data-cy={`${dataCy}-target`}>
							<Pill variant={PillVariant.NEUTRAL}>{targetLabel}</Pill>
						</span>
					)}
					<span data-cy={`${dataCy}-count`}>
						<Detail size="1" color="auxiliary">
							{countLabel}
						</Detail>
					</span>
				</HeaderEnd>
			</Header>
			{expanded && (
				<Body data-cy={`${dataCy}-body`} data-testid={`${dataCy}-body`}>
					{notice && (
						<NoticeRow>
							<span data-cy={`${dataCy}-notice`} data-testid={`${dataCy}-notice`}>
								<Text color="error">{notice}</Text>
							</span>
							{noticeAction && (
								<Button emphasis="medium" data-cy={`${dataCy}-retry`} onPress={noticeAction.onPress}>
									{noticeAction.label}
								</Button>
							)}
						</NoticeRow>
					)}
					{groups.length === 0 && notice ? null : groups.length === 0 && emptyText ? (
						<span data-cy={`${dataCy}-empty`} data-testid={`${dataCy}-empty`}>
							<Text color="auxiliary">{emptyText}</Text>
						</span>
					) : (
						groups.map(group => (
							<Group
								key={group.key}
								data-cy={`${dataCy}-group-${group.key}`}
								data-testid={`${dataCy}-group-${group.key}`}
							>
								<GroupLabel>
									<Detail size="2" noWrap>
										{group.label}
									</Detail>
								</GroupLabel>
								{blockedReasonOf(group) && (
									<span
										id={`${dataCy}-group-${group.key}-blocked`}
										data-cy={`${dataCy}-group-${group.key}-blocked`}
									>
										<Text size="2" color="auxiliary">
											{blockedReasonOf(group)}
										</Text>
									</span>
								)}
								{group.rows.length === 0
									? group.emptyText && (
											<span
												data-cy={`${dataCy}-${group.key.toLowerCase()}-empty`}
												data-testid={`${dataCy}-${group.key.toLowerCase()}-empty`}
											>
												<Text color="auxiliary">{group.emptyText}</Text>
											</span>
									  )
									: group.rows.map(row => (
											<Row key={row.key} data-cy={`${row.dataCy}-row`}>
												<TokenCell>
													<NovaButtonSizer $size="row" {...keepFocus}>
														<Button
															emphasis="medium"
															disabled={Boolean(row.disabledReason)}
															title={row.disabledReason ?? undefined}
															aria-describedby={reasonIdOf(row, group, dataCy)}
															data-cy={row.dataCy}
															onPress={() => {
																if (!row.disabledReason) {
																	row.onInsert();
																}
															}}
														>
															{row.token}
														</Button>
													</NovaButtonSizer>
												</TokenCell>
												<DescriptionCell>
													<Text size="2">{row.description}</Text>
													{row.disabledReason && row.disabledReason !== blockedReasonOf(group) && (
														<NovaVisuallyHidden id={`${row.dataCy}-reason`}>
															{row.disabledReason}
														</NovaVisuallyHidden>
													)}
													{row.secondary && (
														<Detail size="1" color="auxiliary">
															{row.secondary}
														</Detail>
													)}
													{(row.badges ?? []).map(badge => (
														<span key={badge}>
															<Pill variant={PillVariant.NEUTRAL}>{badge}</Pill>
														</span>
													))}
												</DescriptionCell>
											</Row>
									  ))}
							</Group>
						))
					)}
				</Body>
			)}
		</Container>
	);
}
